import { css } from '@emotion/core';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Button from '../components/Button';
import EmailField from '../components/EmailField';
import PageContainer from '../components/PageContainer';
import { useAppMetadataContext } from '../components/AppMetadataContextWrapper';
import PasswordField from '../components/PasswordField';
import hasValidASCIICharacters from '../services/hasValidASCIICharacters';
import UsernameMask from '../components/UsernameMask';
import SelfRegistrationLink from './SelfRegistrationLink';
import ResponseErrorTypes from '../constants/ResponseErrorTypes';
import ResponseStatus from '../constants/ResponseStatus';
import { isValidEmail } from '../services/emailUtils';
import { showErrorNotification } from '../services/errorUtils';
import useMessageService from '../services/i18n/useMessageService';
import LoadingScreenContext from '../services/LoadingScreenContext';
import useIsESigLoginContext from '../services/useIsESigLoginContext';
import useLoginPrefillUser from '../services/useLoginPrefillUser';
import useNetworkErrorNotification from '../services/useNetworkErrorNotification';
import useUserProfile from '../services/useUserProfile';
import useRequestState from '../services/useRequestState';
import { login as loginApi, loginWithVerificationCode } from '../veevaIdApi';
import UnauthenticatedRouteLanguageContext from '../services/UnauthenticatedRouteLanguageContext';
import { UnauthenticatedPaths } from '../constants/Paths';
import useUrlQueryParams from '../services/useUrlQueryParams';
import convertRequestStateToSearchParams from '../services/convertRequestStateToSearchParams';
import VerifyCodePage from './verify-code-page';
import buildAndSubmitForm from '../services/buildAndSubmitForm';
import getTargetAttribute from '../services/getTargetAttribute';
import SearchParams from '../constants/SearchParams';
import useUsername from '../services/useUsername';

const propTypes = {
    login: PropTypes.func,
    onPostLoginSuccess: PropTypes.func,
    onSwitchUser: PropTypes.func,
    prefillUser: PropTypes.shape({
        username: PropTypes.string.isRequired,
    }),
    userProfile: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        reAuthenticate: PropTypes.bool,
        username: PropTypes.string,
    }),
};

export function LoginPage({
    login = loginApi,
    prefillUser,
    userProfile,
    onPostLoginSuccess,
    onSwitchUser,
}) {
    const [
        [
            forgotPasswordLabel = 'Having trouble signing in?',
            loginButtonLabel = 'Log In',
            welcomeBackLabel = 'Welcome Back',
            welcomeLabel = 'Welcome',
            invalidPasswordLabel = 'Your login has failed. The email or password may be incorrect.',
            reAuthenticateLabel = "Let's Check That This Is You",
            invalidEmailLabel = 'Invalid email format entered',
            dontHaveAccountLabel = `Don't have an account?`,
            dontHaveAccountLink = `Register here`,
            nonVaultAppLoginTitle = 'Login to {0}',
        ],
    ] = useMessageService([
        'forgot_password.having_trouble_login',
        'general.log_in',
        'general.welcome_back',
        'general.welcome',
        'login.invalid_password',
        'login.re_authenticate',
        'general.invalid_email',
        'register.dont_have_account',
        'register.register_here_link',
        'login.non_vault_app_page_title',
    ]);
    const history = useHistory();
    const { showNotification: showNetworkErrorNotification } = useNetworkErrorNotification();
    const [showVerifyCodePage, setShowVerifyCodePage] = useState(false);
    const [username, setUsername] = useUsername({ prefillUser, userProfile });
    const [password, setPassword] = useState('');
    const [usernameFieldError, setUsernameFieldError] = useState(false);
    const [usernameFieldErrorMessage, setUsernameFieldErrorMessage] = useState(false);
    const [passwordFieldError, setPasswordFieldError] = useState(false);
    const { loading, setLoading } = useContext(LoadingScreenContext);
    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);
    const searchParams = useUrlQueryParams();
    const { appMetadata, isNonVaultApp } = useAppMetadataContext();
    const [showRegistrationLink, setShowRegistrationLink] = useState(
        !searchParams.get(SearchParams.HIDE_REGISTRATION),
    );
    const requestState = useRequestState();
    const requestStateParams = convertRequestStateToSearchParams(requestState);

    useEffect(() => {
        if (!userProfile) {
            setUsernameFieldErrorMessage(false);
            setUsernameFieldError(false);
            setPasswordFieldError(false);
        }
    }, [userProfile]);

    useEffect(() => {
        setUsernameFieldErrorMessage(false);
        setUsernameFieldError(false);
        setPasswordFieldError(false);
    }, [messageLanguage]);

    const handleSwitchUsername = () => {
        setUsername('');
        setPassword('');
        setUsernameFieldErrorMessage(false);
        onSwitchUser?.();

        // users who swap off a user profile might want to make a new account, so prevent hiding the self-register link
        if (!showRegistrationLink && searchParams.get(SearchParams.HIDE_REGISTRATION)) {
            searchParams.delete(SearchParams.HIDE_REGISTRATION);
            history.replace({
                search: searchParams.toString(),
            });

            // Keep search params and state in sync
            setShowRegistrationLink(true);
        }
    };

    const handleChangeUsername = (value) => {
        setUsername(value);
        setUsernameFieldErrorMessage(false);
        setUsernameFieldError(false);
        setPasswordFieldError(false);
    };

    const handleChangePassword = (value) => {
        setPassword(value);
        setUsernameFieldErrorMessage(false);
        setUsernameFieldError(false);
        setPasswordFieldError(false);
    };

    const handleErrorResponse = (errors) => {
        const error = errors[0];
        switch (error.type) {
            case ResponseErrorTypes.VEEVAID_UNEXPECTED_ERROR:
            case ResponseErrorTypes.VEEVAID_USERPROFILE_NOT_FOUND: {
                const i18nOptions = { language: messageLanguage };
                return showErrorNotification(errors, i18nOptions);
            }
            default: {
                setUsernameFieldError(true);
                return setPasswordFieldError(true);
            }
        }
    };

    const handleAuthenticationResponse = (responseStatus, data, errors) => {
        switch (responseStatus) {
            case ResponseStatus.SUCCESS: {
                if (data?.verifyCode) {
                    setShowVerifyCodePage(true);
                    setLoading(false);
                } else {
                    onPostLoginSuccess?.();
                    buildAndSubmitForm('/ui/code', requestState);
                }
                break;
            }
            case ResponseStatus.FAILURE:
                handleErrorResponse(errors);
                setLoading(false);
                break;
            case ResponseStatus.UI_NETWORK_ERROR:
                showNetworkErrorNotification();
                setLoading(false);
                break;
            default:
            // No-op
        }
    };

    const validateEmailAndPasswordFormats = () => {
        const validEmail = isValidEmail(username) || userProfile?.username;
        const validPassword = hasValidASCIICharacters(password);

        if (validEmail && validPassword) {
            setUsernameFieldErrorMessage(false);
            setUsernameFieldError(false);
            setPasswordFieldError(false);
        } else {
            setUsernameFieldErrorMessage(!validEmail);
            setPasswordFieldError(!validPassword);
        }

        return validEmail && validPassword;
    };

    const handleSubmit = async () => {
        const validEmailAndPasswordFormats = validateEmailAndPasswordFormats();

        if (validEmailAndPasswordFormats) {
            setUsername(username?.trim());
            setLoading(true);
            const { responseStatus, data, errors } = await login(
                username?.trim() || userProfile?.username,
                password,
                requestState,
            );
            handleAuthenticationResponse(responseStatus, data, errors);
        }
    };

    const handleVerifyCodeSubmitStart = () => {
        setLoading(true);
    };

    const handleVerifyCodeSubmit = (code) => {
        return loginWithVerificationCode(code, requestState);
    };

    /**
     * Keeping loading state on success to disable page interaction while
     * buildAndSubmitForm performs an async form submission for a redirect.
     */
    const handleSubmitSuccess = () => {
        onPostLoginSuccess?.();
        buildAndSubmitForm('/ui/code', requestState);
    };

    const handleVerifyCodeSubmitFail = () => {
        setLoading(false);
    };

    if (showVerifyCodePage) {
        return (
            <VerifyCodePage
                onSubmitStart={handleVerifyCodeSubmitStart}
                onSubmit={handleVerifyCodeSubmit}
                onSubmitSuccess={handleSubmitSuccess}
                onSubmitFail={handleVerifyCodeSubmitFail}
            />
        );
    }

    const getPageTitle = () => {
        if (isNonVaultApp) {
            return nonVaultAppLoginTitle?.replace('{0}', appMetadata.app_name);
        }

        if (!userProfile) {
            return welcomeLabel;
        }

        return userProfile?.reAuthenticate ? reAuthenticateLabel : welcomeBackLabel;
    };

    return (
        <PageContainer title={getPageTitle()}>
            {userProfile ? (
                <UsernameMask
                    username={userProfile.username}
                    firstName={userProfile.firstName}
                    lastName={userProfile.lastName}
                    onSwitchUser={handleSwitchUsername}
                    showSwitchUser={!userProfile.reAuthenticate}
                />
            ) : (
                <EmailField
                    error={usernameFieldError}
                    errorMessage={usernameFieldErrorMessage ? invalidEmailLabel : undefined}
                    onChange={handleChangeUsername}
                    onKeyUp={handleSubmit}
                    value={username}
                />
            )}
            <PasswordField
                errorMessage={passwordFieldError ? invalidPasswordLabel : undefined}
                onChange={handleChangePassword}
                onKeyUp={handleSubmit}
                value={password}
            />
            <Button
                type="submit"
                onClick={handleSubmit}
                disabled={!username || !password || loading}
                data-target={getTargetAttribute('login-button')}
            >
                {loginButtonLabel}
            </Button>
            {!userProfile?.reAuthenticate && (
                <Link
                    css={({ blue, linkFontSize }) => {
                        return css`
                            display: inline-block;
                            width: 100%;
                            margin-top: 1rem;
                            font-size: ${linkFontSize};
                            color: ${blue};
                            text-align: center;
                            text-decoration: none;
                        `;
                    }}
                    to={{
                        pathname: UnauthenticatedPaths.FORGOT_PASSWORD,
                        search: requestStateParams,
                    }}
                >
                    {forgotPasswordLabel}
                </Link>
            )}
            {showRegistrationLink && (
                <SelfRegistrationLink
                    linkLabel={dontHaveAccountLink}
                    requestStateParams={requestStateParams}
                    textLabel={dontHaveAccountLabel}
                />
            )}
        </PageContainer>
    );
}

LoginPage.propTypes = propTypes;

export default function LoginPageContainer() {
    const [userProfile, handleSwitchUser] = useUserProfile();
    const isESigLoginContext = useIsESigLoginContext();
    const [prefillUser, handlePostLoginSuccess] = useLoginPrefillUser();

    if (isESigLoginContext) {
        return <LoginPage />;
    }

    if (prefillUser) {
        return <LoginPage prefillUser={prefillUser} onPostLoginSuccess={handlePostLoginSuccess} />;
    }

    return <LoginPage userProfile={userProfile} onSwitchUser={handleSwitchUser} />;
}
